// Public legal pages — /terms and /refund-policy. Content is admin-editable
// HTML from GET /legal/{slug} (Admin → Legal Pages), rendered via SafeHTML.
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar, Footer } from '../components/Layout';
import { SafeHTML } from '../components/SafeHTML';
import SEO from '../components/SEO';
import { useTranslation } from '../context/TranslationContext';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const LegalPage = ({ slug }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPage(null);
    setError(false);
    axios.get(`${API_URL}/legal/${slug}`)
      .then((res) => setPage(res.data))
      .catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const otherLink = slug === 'terms'
    ? { to: '/refund-policy', label: t('legal_refund_link', 'Refund & Cancellation Policy') }
    : { to: '/terms', label: t('legal_terms_link', 'Terms & Conditions') };

  return (
    <div className="min-h-screen bg-background" data-testid={`legal-page-${slug}`}>
      <SEO
        title={page?.title || (slug === 'terms' ? 'Terms & Conditions' : 'Refund & Cancellation Policy')}
        url={`/${slug}`}
      />
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
          {error ? (
            <p className="text-center text-muted-foreground">
              {t('legal_load_fail', 'Could not load this page. Please try again later.')}
            </p>
          ) : !page ? (
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-muted w-2/3 rounded" />
              <div className="h-4 bg-muted w-1/3 rounded" />
              <div className="h-64 bg-muted rounded" />
            </div>
          ) : (
            <>
              <h1 className="text-3xl md:text-4xl font-serif font-medium mb-2">{page.title}</h1>
              {page.effective_date && (
                <p className="text-sm text-muted-foreground mb-8">
                  {t('legal_effective_date', 'Effective Date')}: {formatDate(page.effective_date)}
                </p>
              )}
              <SafeHTML
                html={page.content}
                className="leading-relaxed text-foreground/90 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2 [&_a]:text-accent [&_a]:underline"
              />
              <p className="mt-10 pt-6 border-t text-sm text-muted-foreground">
                {t('legal_see_also', 'See also:')}{' '}
                <Link to={otherLink.to} className="text-accent underline hover:no-underline">{otherLink.label}</Link>
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
